import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FaUser, FaEnvelope, FaLock } from "react-icons/fa";
import { CheckCircle } from "lucide-react";
import { trackMetaEvent } from "../utils/metaPixel";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://upsc-backend-353e.onrender.com";

const SignupPage = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    acceptedTerms: false,
  });

  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [emailToResend, setEmailToResend] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  // ✅ Success screen
  const [signupSuccess, setSignupSuccess] = useState(false);

  const [darkMode, setDarkMode] = useState(() => {
const saved = localStorage.getItem("theme");
return saved === "dark";
});
useEffect(() => {
if (darkMode) {
document.documentElement.classList.add("dark");
localStorage.setItem("theme","dark");
} else {
document.documentElement.classList.remove("dark");
localStorage.setItem("theme","light");
}
}, [darkMode]);
  
  // Countdown for resend & redirect
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => setResendCooldown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // === Username availability check ===
  useEffect(() => {
    if (formData.username.length <= 2) {
      setUsernameAvailable(null);
      return;
    }

    const delay = setTimeout(() => {
      setCheckingUsername(true);
      axios
        .post(`${API_BASE}/api/auth/check-username`, { username: formData.username })
        .then((res) => setUsernameAvailable(res.data.available))
        .catch(() => setUsernameAvailable(false))
        .finally(() => setCheckingUsername(false));
    }, 500);

    return () => clearTimeout(delay);
  }, [formData.username]);

  // === Password strength check ===
  useEffect(() => {
    const strongRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&]).{8,}$/;
    if (!formData.password) setPasswordStrength("");
    else if (formData.password.length < 8) setPasswordStrength("Too short");
    else if (strongRegex.test(formData.password)) setPasswordStrength("Strong");
    else setPasswordStrength("Weak");
  }, [formData.password]);

  // === Input handler ===
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // === Signup & Payment ===
  const handleSubmit = async (e) => {
  e.preventDefault();

  if (usernameAvailable === false)
    return toast.error("Username already taken.");

  if (passwordStrength !== "Strong")
    return toast.error(
      "Password must be at least 8 characters, include uppercase, lowercase, number & special character."
    );

  if (!formData.acceptedTerms)
    return toast.error("Please accept the Terms & Conditions.");

  try {
    setLoading(true);

    await axios.post(`${API_BASE}/api/auth/signup`, {
  ...formData,
  isPremium: false,
});

// ✅ Meta Pixel Registration Event
trackMetaEvent("CompleteRegistration");

setEmailToResend(formData.email);
setSignupSuccess(true);
setResendCooldown(60);

    toast.success("🎉 Account created successfully!", { duration: 5000 });
    toast("📩 Verification email sent. Please check your inbox.", {
      icon: "✅",
      duration: 6000,
    });

  } catch (err) {
    console.error("Signup error:", err);
    toast.error(err.response?.data?.message || "Signup failed. Try again.");
  } finally {
    setLoading(false);
  }
};

  // === Resend Verification ===
  const handleResendVerification = async () => {
    if (!emailToResend)
      return toast.error("No email found to resend verification.");

    if (resendCooldown > 0)
      return toast.error(`Please wait ${resendCooldown}s before resending.`);

    try {
      setResendLoading(true);
      const res = await axios.post(`${API_BASE}/api/auth/resend-verification`, {
        email: emailToResend,
      });
      toast.success(res.data.message || "Verification email sent.");
      setResendCooldown(60);
    } catch (err) {
      console.error("Resend verification error:", err);
      toast.error(err.response?.data?.message || "Failed to resend email.");
    } finally {
      setResendLoading(false);
    }
  };

  // === Success Screen ===
if (signupSuccess) {
  return (
    
    <div className="grid
lg:grid-cols-2
items-center
min-h-screen
relative
z-10 bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700 text-center p-6">
      <CheckCircle className="w-24 h-24 text-green-600 dark:text-green-400 animate-bounce mb-4" />
      <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
        Signup Successful! 🎉
      </h2>
      <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
        A verification email has been sent to{" "}
        <span className="font-semibold text-blue-700 dark:text-yellow-300">
          {emailToResend}
        </span>
        . Please check your inbox and verify your account before logging in.
      </p>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        {/* ✅ Go to verification page */}
        <button
          onClick={() =>
            navigate(
              `/verify-email?type=signup&email=${encodeURIComponent(
                emailToResend
              )}`
            )
          }
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition"
        >
          Go to Verification Page
        </button>

        {/* ✅ Resend verification email */}
        <button
          onClick={handleResendVerification}
          disabled={resendLoading || resendCooldown > 0}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition disabled:opacity-50"
        >
          {resendCooldown > 0
            ? `Resend in ${resendCooldown}s`
            : resendLoading
            ? "Sending..."
            : "Resend Verification Email"}
        </button>

        {/* ✅ Go to login (if already verified) */}
        <button
          onClick={() => navigate("/login")}
          className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 text-blue-900 dark:text-yellow-300 px-6 py-3 rounded-lg font-semibold transition"
        >
          Go to Login Page
        </button>
      </div>

      <p className="mt-8 text-gray-500 dark:text-gray-400 text-sm">
        Didn’t get the mail? Check your spam folder or try resending above.
      </p>

      <p className="text-xs mt-10 text-gray-400">
        © 2025 UPSC Journey. All rights reserved.
      </p>
    </div>
  );
}

  // === Signup Form (default state) ===
  return (
    <div
className="
min-h-screen
relative
overflow-hidden

bg-gradient-to-br
from-yellow-50
via-white
to-orange-50

dark:from-gray-950
dark:via-gray-900
dark:to-black

transition-colors
duration-700
"
>
  {/* Background Glow */}

<div
className="
absolute
top-[-150px]
left-[-120px]

w-[500px]
h-[500px]

bg-yellow-400/20
blur-3xl
rounded-full
pointer-events-none
"
/>

<div
className="
absolute
bottom-[-180px]
right-[-120px]

w-[450px]
h-[450px]

bg-blue-500/10
blur-3xl
rounded-full
pointer-events-none
"
/>
  
 <div
className="
grid
lg:grid-cols-2

items-center

min-h-screen

relative
z-10

px-6
"

>

{/* LEFT SIDE */}
<div
className="
hidden
lg:flex

flex-col
justify-center

px-20
"
>

<img
src="/logo.png"
alt=""
className="w-20 mb-6"
/>

<h1
className="
text-5xl
font-black

text-gray-900
dark:text-yellow-50
"
>
Start Your
UPSC Journey
</h1>

<p
className="
mt-6

text-xl

text-gray-700
dark:text-gray-200
"
>
One place for editorials,
notes, MCQs,
answer writing and tests.
</p>

<div
className="
mt-10
space-y-5

text-gray-800
dark:text-yellow-50

[&_div]:rounded-2xl
[&_div]:p-4

dark:[&_div]:bg-white/5
"
>

<div>✓ Free signup</div>

<div>✓ Premium upgrade later</div>

<div>✓ Daily learning system</div>

<div>✓ Track preparation</div>

</div>

</div>
      <div
className="
w-full
max-w-md

mx-auto

p-8

bg-white/85
dark:bg-gray-900/92

backdrop-blur-2xl

border
border-white/20

dark:border-yellow-400/10

rounded-[32px]

shadow-[0_20px_70px_rgba(0,0,0,0.14)]

text-gray-900
dark:text-gray-100
"
>
  <div className="flex justify-end mb-4">

<button
type="button"
onClick={() => setDarkMode(!darkMode)}

className="
w-11
h-11

rounded-2xl

bg-gray-100
dark:bg-gray-800

hover:scale-105

transition

flex
items-center
justify-center
"
>

{darkMode ? (

<span className="text-yellow-400 text-xl">
☀️
</span>

) : (

<span className="text-gray-800 text-xl">
🌙
</span>

)}

</button>

</div>
        <h2 className="text-3xl font-extrabold text-blue-900 dark:text-yellow-400 text-center mb-6">
          Create Your Account
        </h2>

        <p className="
mt-2
mb-8
text-center
text-gray-500
dark:text-gray-400
">
Start free and upgrade anytime
</p>

        <div className="mb-4 text-sm text-center text-green-700 dark:text-green-400 font-medium bg-green-100 dark:bg-gray-800 px-4 py-2 rounded">
  🎉 Free signup — explore the platform before upgrading
</div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username */}
          <div className="relative">
            <FaUser className="absolute top-3.5 left-3 text-blue-800 dark:text-yellow-400" />
            <input
              type="text"
              name="username"
              placeholder="Username"
              value={formData.username}
              onChange={handleChange}
              required
              className={`w-full pl-10 pr-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 ${
                usernameAvailable === false
                  ? "border-red-500 focus:ring-red-400"
                  : "border-gray-300 dark:border-gray-700 focus:ring-blue-400 dark:focus:ring-yellow-400"
              }`}
            />
            {checkingUsername && (
              <p className="text-sm text-gray-500 mt-1">Checking...</p>
            )}
            {usernameAvailable === false && (
              <p className="text-sm text-red-600 mt-1">Username already taken</p>
            )}
            {usernameAvailable && (
              <p className="text-sm text-green-600 mt-1">Username available</p>
            )}
          </div>

          {/* Email */}
          <div className="relative">
            <FaEnvelope className="absolute top-3.5 left-3 text-blue-800 dark:text-yellow-400" />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-yellow-400"
            />
          </div>

          {/* Password */}
          <div className="relative">
            <FaLock className="absolute top-3.5 left-3 text-blue-800 dark:text-yellow-400" />
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Set a new password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full pl-10 pr-16 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-yellow-400"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute top-2 right-3 text-blue-700 dark:text-yellow-400 text-sm font-semibold hover:underline"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>

          {/* Password strength */}
          {formData.password && (
            <p
              className={`text-sm ${
                passwordStrength === "Strong"
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              Password strength: {passwordStrength}
            </p>
          )}

          {/* Terms */}
          <label className="flex items-center text-sm">
            <input
              type="checkbox"
              name="acceptedTerms"
              checked={formData.acceptedTerms}
              onChange={handleChange}
              className="mr-2 accent-blue-900 dark:accent-yellow-400"
            />
            I accept the{" "}
            <Link
              to="/terms"
              target="_blank"
              className="text-blue-900 dark:text-yellow-400 font-semibold hover:underline ml-1"
            >
              Terms & Conditions
            </Link>
          </label>

          <button
            type="submit"
            disabled={loading || usernameAvailable === false}
            className="w-full bg-blue-900 hover:bg-blue-800 dark:bg-yellow-400 dark:hover:bg-yellow-300 text-white dark:text-gray-900 py-2 rounded-lg font-semibold transition disabled:opacity-50"
          >
            {loading ? "Signing Up..." : "Create Free Account"}
          </button>
          <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
  Upgrade to Premium anytime from your dashboard
</p>

          {/* Back Button */}
          <button
            type="button"
            onClick={() => navigate("/")}
            className="
w-full

py-3
mt-2

rounded-xl

bg-gray-100
hover:bg-gray-200

dark:bg-gray-800
dark:hover:bg-gray-700

text-gray-900
dark:text-yellow-100

border
border-gray-300
dark:border-yellow-400/15

font-semibold

transition
"
          >
            ← Back to Homepage
          </button>

          <p className="text-sm text-center mt-4">
            Already have an account?{" "}
            <span
              onClick={() => navigate("/login")}
              className="text-blue-900 dark:text-yellow-400 font-medium cursor-pointer hover:underline"
            >
              Log in here
            </span>
          </p>
        </form>
      </div>
      </div>
      
   {/* ✅ Footer */}
  <footer className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400">
    © {new Date().getFullYear()} UPSC Journey. All rights reserved.
  </footer>
    </div>
  );
};

export default SignupPage;
